import React, { useState, useMemo } from 'react';
import { Save, Info, Check, Maximize2, RotateCw, RotateCcw, Type, Palette, Video, Image as ImageIcon } from 'lucide-react';
import type { Clip } from '../../types';

interface MasterTabProps {
    currentScope: any;
    onUpdate: (updates: Partial<Clip>) => void;
    onSaveMaster: (data: any) => void;
}

export const MasterTab: React.FC<MasterTabProps> = ({ currentScope, onUpdate, onSaveMaster }) => {
    const [isSaved, setIsSaved] = useState(false);
    const [name, setName] = useState(currentScope.name || "Modèle Sans Nom");

    // Transform State
    const transform = currentScope.transform || { scale: 1, rotation: 0 };
    const [localScale, setLocalScale] = useState(transform.scale ?? 1);
    const [localRotation, setLocalRotation] = useState(transform.rotation ?? 0);

    // Styling State (Global for Synthe)
    const [localColor, setLocalColor] = useState('#FFFFFF');
    const [localFont, setLocalFont] = useState('Arial, sans-serif');

    const handleSave = () => {
        setIsSaved(true);
        setTimeout(() => setIsSaved(false), 2000);
        onSaveMaster({ name });
    };

    const handleResetTransform = () => {
        const newTransform = { ...transform, scale: 1, rotation: 0 };
        onUpdate({ transform: newTransform });
        setLocalScale(1);
        setLocalRotation(0);
    };

    const handleScaleChange = (val: number) => {
        setLocalScale(val);
        onUpdate({ transform: { ...transform, scale: val } });
    };

    const handleRotationChange = (val: number) => {
        setLocalRotation(val);
        onUpdate({ transform: { ...transform, rotation: val } });
    };

    // Update all text clips style
    const handleStyleChange = (updates: any) => {
        if (!currentScope.syntheData?.clips) return;

        const newHelperClips = currentScope.syntheData.clips.map((c: any) => {
            if (c.type === 'text') {
                return { ...c, style: { ...c.style, ...updates } };
            }
            return c;
        });

        onUpdate({
            syntheData: {
                ...currentScope.syntheData,
                clips: newHelperClips
            }
        });
    };

    // Update specific text content
    const handleTextContentChange = (childId: string, txt: string) => {
        if (!currentScope.syntheData?.clips) return;

        const newHelperClips = currentScope.syntheData.clips.map((c: any) => {
            if (c.id === childId) {
                return { ...c, text: txt };
            }
            return c;
        });

        onUpdate({
            syntheData: {
                ...currentScope.syntheData,
                clips: newHelperClips
            }
        });
    };

    const nestedClips = currentScope.syntheData?.clips || [];
    const texts = nestedClips.filter((c: any) => c.type === 'text');
    const visuals = nestedClips.filter((c: any) => c.type === 'image' || c.type === 'video');

    const availableFonts = [
        { name: 'LVMH', family: 'LVMH, sans-serif' },
        { name: 'Arial', family: 'Arial, sans-serif' },
        { name: 'Helvetica', family: 'Helvetica, sans-serif' },
        { name: 'Times New Roman', family: '"Times New Roman", serif' },
        { name: 'Courier New', family: '"Courier New", monospace' },
    ];

    return (
        <div className="h-full flex flex-col bg-[#09090b] text-white">
            {/* Header */}
            <div className="px-5 py-4 border-b border-white/10 bg-black/20 sticky top-0 z-10 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${currentScope.type === 'synthe' ? 'bg-purple-500/20 text-purple-400' : 'bg-blue-500/20 text-blue-400'}`}>
                        {currentScope.type === 'synthe' ? <Type className="w-5 h-5" /> : <Info className="w-5 h-5" />}
                    </div>
                    <div>
                        <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">Master</span>
                        <h2 className="text-sm font-bold">{name}</h2>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-8">

                {/* 1. Contenu Synthé & Style (TOP) */}
                <section className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Type className="w-4 h-4 text-gray-400" />
                            <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Contenu Synthé</h3>
                        </div>
                    </div>

                    {/* Global Style Controls (Embedded within Content Section) */}
                    {texts.length > 0 && (
                        <div className="bg-white/5 rounded-xl p-3 border border-white/5 grid grid-cols-2 gap-3 mb-4">
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase block mb-1">Police</label>
                                <select
                                    className="w-full bg-black border border-white/20 rounded text-xs p-1 outline-none focus:border-blue-500"
                                    value={localFont}
                                    onChange={(e) => {
                                        setLocalFont(e.target.value);
                                        handleStyleChange({ fontFamily: e.target.value });
                                    }}
                                >
                                    {availableFonts.map(f => (
                                        <option key={f.name} value={f.family}>{f.name}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-gray-500 uppercase block mb-1">Couleur</label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        value={localColor}
                                        onChange={(e) => {
                                            setLocalColor(e.target.value);
                                            handleStyleChange({ color: e.target.value });
                                        }}
                                        className="w-6 h-6 rounded cursor-pointer border-none bg-transparent"
                                    />
                                    <input
                                        type="text"
                                        value={localColor}
                                        onChange={(e) => {
                                            setLocalColor(e.target.value);
                                            handleStyleChange({ color: e.target.value });
                                        }}
                                        className="w-full bg-transparent text-xs font-mono border-b border-white/20 focus:border-white outline-none"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {texts.map((t: any, i: number) => (
                        <div key={t.id} className="space-y-1">
                            <label className="text-[10px] text-gray-500 uppercase pl-1">Texte {i + 1}</label>
                            <textarea
                                value={t.text || ''}
                                onChange={(e) => handleTextContentChange(t.id, e.target.value)}
                                className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:border-blue-500/50 focus:bg-white/10 transition-colors resize-none min-h-[60px]"
                                placeholder="Saisir le texte..."
                            />
                        </div>
                    ))}

                    {texts.length === 0 && (
                        <p className="text-xs text-gray-600 italic">Aucun champ texte éditable.</p>
                    )}
                </section>

                {/* 2. Media List (Read Only) */}
                {visuals.length > 0 && (
                    <section className="space-y-4 pt-4 border-t border-white/10">
                        <div className="flex items-center gap-2 mb-2">
                            <Video className="w-4 h-4 text-gray-400" />
                            <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Médias Requis</h3>
                        </div>
                        <div className="space-y-2">
                            {visuals.map((v: any) => (
                                <div key={v.id} className="flex items-center gap-2 text-xs bg-white/5 p-2 rounded border border-white/5 text-gray-400">
                                    {v.type === 'video' ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
                                    <span>{v.name}</span>
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <div className="h-px bg-white/10 w-full" />

                {/* 3. TRANSFORMATION (Inspector) - BOTTOM */}
                <section className="space-y-4">
                    <div className="flex items-center gap-2 mb-2">
                        <Maximize2 className="w-4 h-4 text-gray-400" />
                        <h3 className="text-xs font-bold uppercase text-gray-400 tracking-wider">Inspector (Transform)</h3>
                    </div>

                    {/* Scale */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/5 space-y-3">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-300">Échelle (Scale)</span>
                            <span className="font-mono text-gray-500">{localScale.toFixed(2)}x</span>
                        </div>
                        <input
                            type="range"
                            min="0.1" max="3" step="0.01"
                            value={localScale}
                            onChange={(e) => handleScaleChange(Number(e.target.value))}
                            className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-blue-500 [&::-webkit-slider-thumb]:rounded-full"
                        />
                    </div>

                    {/* Rotation */}
                    <div className="bg-white/5 rounded-xl p-4 border border-white/5 space-y-3">
                        <div className="flex justify-between items-center text-xs">
                            <span className="text-gray-300">Rotation</span>
                            <span className="font-mono text-gray-500">{Math.round(localRotation)}°</span>
                        </div>
                        <input
                            type="range"
                            min="-180" max="180" step="1"
                            value={localRotation}
                            onChange={(e) => handleRotationChange(Number(e.target.value))}
                            className="w-full h-1.5 bg-gray-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-green-500 [&::-webkit-slider-thumb]:rounded-full"
                        />
                        <div className="flex gap-2 mt-2">
                            <button onClick={() => handleRotationChange(0)} className="bg-white/10 text-[10px] px-2 py-1 rounded hover:bg-white/20">0°</button>
                            <button onClick={() => handleRotationChange(90)} className="bg-white/10 text-[10px] px-2 py-1 rounded hover:bg-white/20">90°</button>
                        </div>
                    </div>

                    <button
                        onClick={handleResetTransform}
                        className="w-full py-2 text-xs font-medium text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                        <RotateCcw className="w-3 h-3" /> Réinitialiser
                    </button>
                </section>

            </div>

            {/* Footer Button */}
            <div className="p-5 border-t border-white/10 bg-black/20 backdrop-blur-sm">
                <button
                    onClick={handleSave}
                    className={`w-full py-3 rounded-xl font-bold text-sm shadow-lg transition-all transform active:scale-95 flex items-center justify-center gap-2 
                        ${isSaved ? 'bg-green-500 text-white' : 'bg-white text-black hover:bg-gray-200'}`}
                >
                    {isSaved ? <><Check className="w-4 h-4" /> Enregistré</> : <><Save className="w-4 h-4" /> Sauvegarder Master</>}
                </button>
            </div>
        </div>
    );
};
